import type { Task } from '@medplum/fhirtypes';
import {
  MISSING_NOTE_TASK_CODE,
  TASK_TYPE_SYSTEM,
  TREATMENT_PLAN_DUE_TASK_CODE,
  TREATMENT_PLAN_RENEWAL_TASK_CODE,
} from '@unify/fhir-model/constants';
import { getTaskTypeCoding } from '../pages/tasks/services/taskFhirMapping';
import {
  APPOINTMENT_REQUEST_TASK,
} from '../pages/client-portal/utils/appointmentRequestTask';
import {
  APPOINTMENT_RESCHEDULE_TASK,
} from '../pages/client-portal/utils/rescheduleRequestTask';

export interface ProviderTaskDestination {
  path: string;
  scrollToMessageId?: string;
}

function taskTypeCode(task: Task): string | undefined {
  return getTaskTypeCoding(task)?.code;
}

export function appointmentIdFromTaskFocus(task: Task): string | undefined {
  const ref = task.focus?.reference;
  if (!ref?.startsWith('Appointment/')) return undefined;
  return ref.slice('Appointment/'.length);
}

export function episodeOfCareIdFromTaskFocus(task: Task): string | undefined {
  const ref = task.focus?.reference;
  if (!ref?.startsWith('EpisodeOfCare/')) return undefined;
  return ref.slice('EpisodeOfCare/'.length);
}

function patientIdFromTask(task: Task): string | undefined {
  const ref = task.for?.reference;
  if (!ref?.startsWith('Patient/')) return undefined;
  return ref.slice('Patient/'.length);
}

function isPortalAppointmentTask(task: Task): boolean {
  const code = taskTypeCode(task);
  return code === APPOINTMENT_REQUEST_TASK.code || code === APPOINTMENT_RESCHEDULE_TASK.code;
}

function isMissingNoteTask(task: Task): boolean {
  return task.code?.coding?.some(
    (c) => c.system === TASK_TYPE_SYSTEM && c.code === MISSING_NOTE_TASK_CODE,
  ) ?? false;
}

function isTreatmentPlanTask(task: Task): boolean {
  return (
    task.code?.coding?.some(
      (c) =>
        c.system === TASK_TYPE_SYSTEM &&
        (c.code === TREATMENT_PLAN_DUE_TASK_CODE || c.code === TREATMENT_PLAN_RENEWAL_TASK_CODE),
    ) ?? false
  );
}

function resolvePortalAppointmentTaskDestination(task: Task): ProviderTaskDestination | null {
  const appointmentId = appointmentIdFromTaskFocus(task);
  if (appointmentId) {
    return { path: `/dashboard/calendar?eventId=${appointmentId}` };
  }

  return null;
}

/** Where a provider should land to action an open Task (calendar, chart, etc.). */
export function resolveProviderTaskDestination(task: Task): ProviderTaskDestination | null {
  if (isPortalAppointmentTask(task)) {
    return resolvePortalAppointmentTaskDestination(task);
  }

  if (isMissingNoteTask(task)) {
    const appointmentId = appointmentIdFromTaskFocus(task);
    if (appointmentId) {
      return { path: `/dashboard/appointments/${appointmentId}` };
    }
  }

  if (isTreatmentPlanTask(task)) {
    const patientId = patientIdFromTask(task);
    const episodeId = episodeOfCareIdFromTaskFocus(task);
    if (patientId && episodeId) {
      const params = new URLSearchParams({ tab: 'chart', episodeId });
      return { path: `/dashboard/clients/${patientId}?${params.toString()}` };
    }
    if (patientId) {
      return { path: `/dashboard/clients/${patientId}?tab=chart` };
    }
  }

  const patientRef = task.for?.reference;
  if (patientRef?.startsWith('Patient/')) {
    const patientId = patientRef.slice('Patient/'.length);
    if (task.focus?.reference?.startsWith('Questionnaire/') && task.id) {
      return { path: `/dashboard/clients/${patientId}/measures/assessment/${task.id}` };
    }
  }

  return null;
}

export function buildProviderTaskNavigationUrl(destination: ProviderTaskDestination): string {
  if (!destination.scrollToMessageId) return destination.path;
  const params = new URLSearchParams({ messageId: destination.scrollToMessageId });
  return `${destination.path}?${params.toString()}`;
}
